import { useMemo } from 'react'
import { GroupBase } from 'react-select'

import useGetUsersQuery from 'common/api/queries/useGetUsersQuery'
import MultiSelect from 'common/components/MultiSelect'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import useProjectsQuery from 'features/campaigns/queries/useProjectsQuery'
import useGetTemplatesQuery from 'features/unifiedFlow/api/queries/useGetTemplatesQuery'
import { applyContentLibraryFilters } from 'features/unifiedFlow/store/unifiedFlowSlice'

const GroupLabel = (
  data: GroupBase<{
    id: string
    label: string
    value: string
  }>
) => (
  <div>
    <span>{data.label}</span>
  </div>
)

type TemplateOption = {
  id: string
  label: string
  value: string
}

type TemplateGroup = {
  label: string
  value: string
  id: string
  options: TemplateOption[]
}

type Props = {
  onFilterChange?: () => void
}
const Filters = ({ onFilterChange }: Props) => {
  const dispatch = useAppDispatch()
  const {
    selectedProjects,
    selectedOwners,
    selectedTypes,
    selectedTemplates,
    selectedStatuses,
  } = useAppSelector((state) => state.unifiedFlow.contentLibraryFilters)

  const { data: templates } = useGetTemplatesQuery({ withInactive: true })
  const templateItems = useMemo(
    () =>
      templates
        ? Object.values(
            templates
              .filter((template) => !template.disabled)
              .reduce<Record<string, TemplateGroup>>((acc, template) => {
                const group = acc[template.contentType] || {
                  label: template.contentType,
                  value: template.contentType,
                  id: template.contentType,
                  options: [],
                }
                group.options.push({
                  id: template.name,
                  label: template.name,
                  value: template.name,
                })
                acc[template.contentType] = group
                return acc
              }, {})
          )
        : [],
    [templates]
  )

  const { data: projects } = useProjectsQuery()
  const filteredProjectItems = useMemo(
    () =>
      projects
        ?.filter((project) => !project.deleted)
        ?.map(({ _id, name }) => ({
          label: name,
          value: _id,
          id: _id,
        })) ?? [],
    [projects]
  )

  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery()
  const filteredUsers = useMemo(
    () => users?.filter((user) => !user.deleted && !user.blocked) ?? [],
    [users]
  )

  const owners =
    filteredUsers
      ?.map(({ _id, first_name, last_name }) => ({
        label: `${first_name} ${last_name}`,
        value: _id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []

  return (
    <div className="flex gap-4 flex-wrap flex-grow-0">
      <MultiSelect
        className="w-52 flex-none"
        data-cy="reports-type-select"
        data-testid="project-multiselect"
        name="projects"
        label="Project"
        placeholder="All projects"
        onApply={(value) => {
          dispatch(
            applyContentLibraryFilters({
              selectedProjects: value,
            })
          )
          onFilterChange?.()
        }}
        selectedItems={selectedProjects}
        items={filteredProjectItems}
      />
      <MultiSelect
        className="w-52 flex-none"
        data-cy="reports-type-select"
        data-testid="type-multiselect"
        name="types"
        label="Type"
        placeholder="All types"
        onApply={(value) => {
          dispatch(applyContentLibraryFilters({ selectedTypes: value }))
          onFilterChange?.()
        }}
        selectedItems={selectedTypes}
        items={[
          { label: 'Optimise', value: 'optimise', id: 'optimise' },
          { label: 'Personalise', value: 'personalise', id: 'personalise' },
          { label: 'Content', value: 'content', id: 'content' },
        ]}
      />
      <MultiSelect
        className="w-52 flex-none"
        data-cy="reports-type-select"
        data-testid="template-multiselect"
        name="templates"
        label="Template"
        placeholder="All templates"
        onApply={(value) => {
          dispatch(
            applyContentLibraryFilters({
              selectedTemplates: value,
            })
          )
          onFilterChange?.()
        }}
        selectedItems={selectedTemplates}
        items={templateItems}
        formatGroupLabel={GroupLabel}
      />
      <MultiSelect
        className="w-52 flex-none"
        data-cy="reports-type-select"
        data-testid="status-multiselect"
        name="statuses"
        label="Status"
        placeholder="All statuses"
        onApply={(value) => {
          dispatch(
            applyContentLibraryFilters({
              selectedStatuses: value,
            })
          )
          onFilterChange?.()
        }}
        selectedItems={selectedStatuses}
        items={[
          { label: 'New language', value: 'New language', id: 'new language' },
          { label: 'Draft', value: 'Draft', id: 'draft' },
          {
            label: 'Pending approval',
            value: 'Pending approval',
            id: 'pending approval',
          },
          {
            label: 'Missing results',
            value: 'Missing results',
            id: 'missing results',
          },
          { label: 'Live', value: 'Live', id: 'live' },
          { label: 'Completed', value: 'Completed', id: 'completed' },
        ]}
      />
      <MultiSelect
        className="w-52 flex-none"
        data-cy="owners-select"
        data-testid="owners-multiselect"
        name="owners"
        label="Owner"
        placeholder="All owners"
        selectedItems={selectedOwners}
        isLoading={isLoadingUsers}
        onApply={(value) => {
          dispatch(
            applyContentLibraryFilters({
              selectedOwners: value,
            })
          )
          onFilterChange?.()
        }}
        items={owners.map(({ label, value }) => ({
          label,
          id: value,
          value,
        }))}
      />
    </div>
  )
}

export default Filters
